import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import { TRowItem } from "../../components/Table/TableRow";
import DamsTable from "../../components/Table/CollapsibleTable";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";
import useAppNavigation from "../../hooks/useAppNavigation";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { updateBatchDistributionCart } from "../../store/reducers/distribution/distributedRecordStore";
import {
  IDistributionBatchRecord,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { DamsAppAsset } from "../../assets/registry";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { AssetDistributionLogsFilter } from "../../common-ui/page-filters/AssetDistributionLogsFilter";
import appRoutePath from "../../config/path";
import { BadgeLabel } from "../../components/Label";
import { DistributionBatchStatus } from "../../config/constants/common";
import { systemUser } from "../../config/constants/system-users";
import { ExportModal } from "../../components/Modals/ExportModal";
import { usePageModels } from "../../hooks/usePageActions";
import { appConfig } from "../../config/config";
import ActionModal from "../../components/Modals/ActionModal";

const OutBoundDistributionPage = () => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const dispatch = useAppDispatch();
  const account = useAppSelector((data) => data.authVerify);
  const isSuperAdmin = account.role === systemUser.SUPER_ADMIN;

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();

  const FetchBatchDistributions = async (query: object) => {
    if (isSuperAdmin) {
      return assetBatchDistributionApi.getBatchDistributions(query);
    }
    return assetBatchDistributionApi.getBatchDistributions(query, {
      from_location_id: account.domain_id,
    });
  };

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<IDistributionBatchRecord[]>(FetchBatchDistributions);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const populateDataSet = (
    data: IDistributionBatchRecord[],
    pagination?: IPagination
  ) => {
    const formatData = data.map((data) => ({
      ...data,
      from_location_domain: `${data.from_location_domain}(${data.from_location})`,
      to_location_domain: `${data.to_location_domain}(${data.to_location})`,
    }));

    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    const result = await FetchBatchDistributions({
      is_deleted: false,
    });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useAppNavigation();

  const toggleEditRecord = (data: TRowItem) => {
    const record = data as IDistributionBatchRecord;

    if (record.distribution_batch_status !== DistributionBatchStatus.pending) {
      alertDialogStore.open({
        title: "Modification Not Allowed",
        subtitle: `This record cannot be modified because its status is "${record.distribution_batch_status}".`,
        isSuccess: false,
        rightBtnTxt: "Okay",
      });
      return;
    }
    dispatch(updateBatchDistributionCart(data as IDistributionBatchRecord));
    actionDialogStore.open({
      title: "Modify Record",
      subtitle: `Click CONTINUE to upate record with batch number ${data.batch_number}`,
      onRightBtnPress: () =>
        navigate({
          path: appRoutePath.OUTBOUND_ASSET_DISTRIBUTIONS_EDIT_PAGE,
          params: { id: data.id },
        }),
      rightBtnTxt: "Continue",
      onLeftBtnPress: () => {},
      leftBtnTxt: "Cancel",
    });
  };

  const toggleDeleteRecord = async (data: TRowItem) => {
    const record = data as IDistributionBatchRecord;

    if (record.distribution_batch_status !== DistributionBatchStatus.pending) {
      alertDialogStore.open({
        title: "Delete Action Denied",
        subtitle: `Records with status "${record.distribution_batch_status}" cannot be modified or deleted.`,
        isSuccess: false,
        rightBtnTxt: "Okay",
      });

      return;
    }
    actionDialogStore.open({
      title: "Delete Record",
      subtitle: ` Once you delete record, ONLY admin can restore. Are you sure you want to proceed?`,
      onRightBtnPress: () => handleDeleteRequest(data.id),
      rightBtnTxt: "Confirm",
      onLeftBtnPress: () => {},
      type: "DELETE",
    });
  };

  async function handleDeleteRequest(id: string) {
    const res = await assetBatchDistributionApi.deleteBatchDistribution(
      id ?? ""
    );
    if (res.success) {
      await fetchData();
      resetSelectedRowItems();
      alertDialogStore.open({
        title: res.success ? "Delete  Record" : "Delete  Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const exportParams = {
        file_format: 'csv' as const,
        ...querySet,
        ...(isSuperAdmin ? {} : { from_location_id: account.domain_id })
      };

      const response = await assetBatchDistributionApi.exportAssetDistributions(exportParams);

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'asset_distributions_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const exportParams = {
        file_format: 'pdf' as const,
        ...querySet,
        ...(isSuperAdmin ? {} : { from_location_id: account.domain_id })
      };

      const response = await assetBatchDistributionApi.exportAssetDistributions(exportParams);

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'asset_distributions_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };

    const bulkDelete = async (id: string) => {
      const nonPendingRecords = selectedRowItems.filter(
        (item) =>
          (item as IDistributionBatchRecord).distribution_batch_status !==
          DistributionBatchStatus.pending
      );

      if (nonPendingRecords.length > 0) {
        return { success: false, message: "One or more items not pending" };
      }
      return assetBatchDistributionApi.deleteBatchDistribution(id);
    };

    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={bulkDelete}
          idKey={"id"}
        />
      </Stack>
    );
  };

  const renderTableLinkActions = ({ item }: { item: TRowItem }) => {
    const toggleDetailButton = async () => {
      navigate({
        path: appRoutePath.OUTBOUND_ASSET_DISTRIBUTIONS_DETAIL_PAGE,
        params: { id: item.id },
      });
    };

    return (
      <Stack direction={"row"} spacing={1} pl={2}>
        <BadgeLabel
          onClick={toggleDetailButton}
          title={"Details"}
          colorType="blue"
          tip="Go details page"
        />
      </Stack>
    );
  };

  function toggleAlertModal() {
    closeModel();
  }

  return (
    <MainPageBase
      pageState={"main"}
      title="Outbound Asset Distributions"
      subtitle="Track outbound distribution batches of assets"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Stack width={"100%"}>
          <AssetDistributionLogsFilter
            ftn={FetchBatchDistributions}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
            isDeleted={true}
          />
          <DamsTable
            tableTitle="Outbound Distribution Logs"
            tableSubtitle=""
            numTableRecords="25"
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            maxRowCells={6}
            onTableItemEdit={toggleEditRecord}
            onTableItemDelete={toggleDeleteRecord}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            selectedRowItems={selectedRowItems}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            headerDropDownMenu={renderHeaderDropDownMenu}
            extraRowComponent={renderTableLinkActions}
          />

          {/* Export Modal */}
          <ExportModal
            show={showExportModal}
            onClose={closeExportModal}
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            isExportingCSV={isExportingCSV}
            isExportingPDF={isExportingPDF}
            title="Export Asset Distributions"
            subtitle="Choose your preferred format to export asset distribution data"
          />

          {/* Alert Modal for export feedback */}
          {models.showAlertModel && (
            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={"continue"}
              onClosePress={closeModel}
            />
          )}
        </Stack>
      </Box>
    </MainPageBase>
  );
};

const tkeys = [
  "from_location_domain",
  "to_location_domain",
  "quantity",
  "distribution_batch_status",
];

const columns = [
  "Distributor Domain",
  "Recipent Domain",
  "Number of Assets",
  "Status",
];

export default OutBoundDistributionPage;